import React, { useEffect, useState, useCallback } from 'react';
import { container } from 'tsyringe';
import Renderer from '../../Components/Renderer';
import TranslationService from '../../Translations/TranslationService';
import DemoService from '../../DemoService';
import { VRButton } from '../../Components/VrButton';

export const VrButton = () => {
    const renderer = container.resolve(Renderer);
    const demoService = container.resolve(DemoService);
    const [isVrSupported, setIsVrSupported] = useState(false);
    const [isWebXRAvailable, setIsWebXRAvailable] = useState(true);
    const [session, setSession] = useState<XRSession | null>(null);

    const sessionInit = {
        optionalFeatures: [
            'local-floor',
            'bounded-floor',
            'hand-tracking',
        ],
    };

    useEffect(() => {
        const checkVrSupport = async () => {
            if ('xr' in navigator) {
                try {
                    const supported = await navigator.xr.isSessionSupported(
                        'immersive-vr',
                    );
                    setIsVrSupported(supported);
                } catch (error) {
                    console.error('Error checking VR support:', error);
                    setIsVrSupported(false);
                }
            } else {
                setIsVrSupported(false);
            }
        };

        checkVrSupport();

        // Use demoService state for availability if needed, similar to original code
        setIsWebXRAvailable(demoService.isWebXRAvailable);

        const handleWebXRAvailabilityChanged = (event: any) => {
            setIsWebXRAvailable(event.isAvailable);
        };

        (demoService as any).addEventListener(
            'webXRAvailabilityChanged',
            handleWebXRAvailabilityChanged,
        );

        return () => {
            (demoService as any).removeEventListener(
                'webXRAvailabilityChanged',
                handleWebXRAvailabilityChanged,
            );
        };
    }, [demoService]);

    const endSession = useCallback(async () => {
        if (session) {
            await session.end();
            setSession(null);
        }
    }, [session]);

    // Handle session end from external sources (like headset button)
    useEffect(() => {
        const handleSessionEnd = () => {
            setSession(null);
            demoService.onWebXRSessionEnd();
        };

        if (session) {
            session.addEventListener('end', handleSessionEnd);
        }

        return () => {
            if (session) {
                session.removeEventListener('end', handleSessionEnd);
            }
        };
    }, [session, demoService]);


    const handleClick = async () => {
        if (session) {
            await endSession();
        } else if (isWebXRAvailable) {
            try {
                const newSession = await VRButton.startSession(renderer.webGLRenderer, sessionInit);
                setSession(newSession);
                demoService.onWebXRSessionStart();
            } catch (error) {
                console.error('WebXR session failed to start:', error);
            }
        } else {
            demoService.sendRequestPopup();
        }
    };

    if (!isVrSupported) {
        return null;
    }

    return (
        <button onClick={handleClick}>
            <span>
                {session
                    ? TranslationService.translate('vr.menu_exit_vr')
                    : isWebXRAvailable
                        ? TranslationService.translate('vr.menu_enter_vr')
                        : TranslationService.translate('vr.menu_enter_vr')}
            </span>
        </button>
    );
};
